import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Scissors, Loader2, Eye, EyeOff } from 'lucide-react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: 'weak' };
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[!@#$%^&*()_+\-=[\]{}|;':",./<>?\\`~]/.test(password)) score += 1;
  const label = score <= 1 ? 'weak' : score === 2 ? 'fair' : score === 3 ? 'good' : 'strong';
  return { score, label };
}

export default function Login() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', salon_name: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const { t } = useLanguage();

  const clearError = (field) => {
    setErrors((e) => {
      const next = { ...e };
      delete next[field];
      delete next.form;
      return next;
    });
  };

  const validateLogin = () => {
    const e = {};
    if (!form.email.trim()) e.email = t('val_email_required');
    else if (!EMAIL_REGEX.test(form.email.trim())) e.email = t('val_email_invalid');
    if (!form.password) e.password = t('val_password_required');
    else if (form.password.length < 6) e.password = t('val_password_min_login');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateSignup = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('val_name_required');
    else if (form.name.trim().length < 2) e.name = t('val_name_min');
    if (!form.salon_name.trim()) e.salon_name = t('val_salon_required');
    else if (form.salon_name.trim().length < 2) e.salon_name = t('val_salon_required');
    if (!form.email.trim()) e.email = t('val_email_required');
    else if (!EMAIL_REGEX.test(form.email.trim())) e.email = t('val_email_invalid');
    if (!form.password) e.password = t('val_password_required');
    else if (form.password.length < 8) e.password = t('val_password_min');
    if (form.password !== form.confirmPassword) e.confirmPassword = t('val_passwords_match');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    const valid = isLogin ? validateLogin() : validateSignup();
    if (!valid) return;
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: form.email.trim(), password: form.password }
        : { email: form.email.trim(), password: form.password, name: form.name.trim(), salon_name: form.salon_name.trim() };
      const res = await api.post(endpoint, payload);
      localStorage.setItem('salon_token', res.data.token);
      localStorage.setItem('salon_user', JSON.stringify(res.data.user));
      toast.success(isLogin ? t('login_welcome_back_toast', { name: res.data.user.name }) : t('login_welcome_toast', { name: res.data.user.name }));
      navigate('/');
    } catch (err) {
      setErrors({ form: err.response?.data?.detail || t('invalid_credentials') });
    } finally {
      setLoading(false);
    }
  };

  const strength = useMemo(() => getPasswordStrength(form.password), [form.password]);
  const strengthLabelKey = strength.label === 'weak' ? 'strength_weak' : strength.label === 'fair' ? 'strength_fair' : strength.label === 'good' ? 'strength_good' : 'strength_strong';

  if (showForgot) {
    return (
      <div className="min-h-screen flex">
        <div className="hidden lg:flex lg:w-1/2 relative bg-[#1a1a2e] items-center justify-center overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1728488447889-13f95adcf5a0?q=80&w=2070&auto=format&fit=crop"
            alt="Salon"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="relative z-10 text-center px-12">
            <div className="inline-flex items-center gap-3 bg-[#f0a500] px-6 py-3 rounded-2xl mb-8">
              <Scissors className="w-8 h-8 text-[#1a1a2e]" />
              <span className="font-poppins text-3xl font-bold text-[#1a1a2e]">SalonSync</span>
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#faf8f5] px-6 py-12">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
            <h2 className="font-poppins text-xl font-bold text-[#1a1a2e] mb-3">{t('forgot_title')}</h2>
            <p className="text-gray-600 mb-4">{t('forgot_body')}</p>
            <p className="text-gray-500 text-sm mb-6">{t('forgot_hint')}</p>
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-xl border-[#f0a500] text-[#f0a500] hover:bg-[#f0a500]/10"
              onClick={() => setShowForgot(false)}
            >
              {t('back_to_login')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left: Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#1a1a2e] items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1728488447889-13f95adcf5a0?q=80&w=2070&auto=format&fit=crop"
          alt="Salon"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 text-center px-12">
          <div className="inline-flex items-center gap-3 bg-[#f0a500] px-6 py-3 rounded-2xl mb-8">
            <Scissors className="w-8 h-8 text-[#1a1a2e]" />
            <span className="font-poppins text-3xl font-bold text-[#1a1a2e]">SalonSync</span>
          </div>
          <h2 className="font-poppins text-4xl font-bold text-white mb-4 leading-tight">
            {t('login_tagline').split('\n').map((line, i) => (
              <span key={i}>{line}<br /></span>
            ))}
          </h2>
          <p className="text-gray-300 text-lg max-w-md mx-auto">
            {t('login_tagline_desc')}
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center bg-[#faf8f5] px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-10">
            <Scissors className="w-7 h-7 text-[#f0a500]" />
            <span className="font-poppins text-2xl font-bold text-[#1a1a2e]">SalonSync</span>
          </div>

          <h1 className="font-poppins text-3xl font-bold text-[#1a1a2e] mb-2">
            {isLogin ? t('welcome_back') : t('create_account')}
          </h1>
          <p className="text-gray-500 text-lg mb-8">
            {isLogin ? t('sign_in_subtitle') : t('create_account_subtitle')}
          </p>

          {errors.form && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {errors.form}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <Label className="text-base font-medium mb-2 block">{t('your_name')}</Label>
                  <Input
                    data-testid="register-name-input"
                    className={`h-14 text-lg rounded-xl ${errors.name ? 'border-red-500' : ''}`}
                    placeholder={t('login_enter_name')}
                    value={form.name}
                    onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); clearError('name'); }}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>
                <div>
                  <Label className="text-base font-medium mb-2 block">{t('salon_name')}</Label>
                  <Input
                    data-testid="register-salon-input"
                    className={`h-14 text-lg rounded-xl ${errors.salon_name ? 'border-red-500' : ''}`}
                    placeholder={t('login_enter_salon')}
                    value={form.salon_name}
                    onChange={(e) => { setForm((f) => ({ ...f, salon_name: e.target.value })); clearError('salon_name'); }}
                  />
                  {errors.salon_name && <p className="mt-1 text-sm text-red-600">{errors.salon_name}</p>}
                </div>
              </>
            )}
            <div>
              <Label className="text-base font-medium mb-2 block">{t('email')}</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                className={`h-14 text-lg rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                placeholder={t('login_enter_email')}
                value={form.email}
                onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); clearError('email'); }}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-base font-medium mb-2 block">{t('password')}</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPass ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  className={`h-14 text-lg rounded-xl pr-12 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder={t('login_enter_pass')}
                  value={form.password}
                  onChange={(e) => { setForm((f) => ({ ...f, password: e.target.value })); clearError('password'); }}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  onClick={() => setShowPass(!showPass)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                >
                  {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {!isLogin && form.password && (
                <div className="mt-2">
                  <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: strength.score <= 1 ? '25%' : strength.score === 2 ? '50%' : strength.score === 3 ? '75%' : '100%',
                        backgroundColor: strength.score <= 1 ? '#dc2626' : strength.score === 2 ? '#ea580c' : strength.score === 3 ? '#ca8a04' : '#16a34a',
                      }}
                    />
                  </div>
                  <p className="text-right text-xs mt-1" style={{ color: strength.score <= 1 ? '#dc2626' : strength.score === 2 ? '#ea580c' : strength.score === 3 ? '#ca8a04' : '#16a34a' }}>
                    {t('password_strength')}: {t(strengthLabelKey)}
                  </p>
                </div>
              )}
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            {!isLogin && (
              <div>
                <Label className="text-base font-medium mb-2 block">{t('confirm_password')}</Label>
                <div className="relative">
                  <Input
                    data-testid="register-confirm-password-input"
                    type={showConfirmPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`h-14 text-lg rounded-xl pr-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    placeholder={t('confirm_password')}
                    value={form.confirmPassword}
                    onChange={(e) => { setForm((f) => ({ ...f, confirmPassword: e.target.value })); clearError('confirmPassword'); }}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    aria-label={showConfirmPass ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
            )}

            {isLogin && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-sm text-[#f0a500] hover:underline"
                  onClick={() => setShowForgot(true)}
                >
                  {t('forgot_password')}
                </button>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-14 text-lg font-semibold rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] shadow-lg gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin shrink-0" />}
              {loading ? (isLogin ? t('signing_in') : t('creating_account')) : isLogin ? t('sign_in') : t('create_account')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => { setIsLogin(!isLogin); setErrors({}); setShowForgot(false); }}
              className="text-[#f0a500] font-medium hover:underline text-base"
            >
              {isLogin ? <>{t('dont_have_account')} {t('sign_up')}</> : <>{t('already_have_account')} {t('sign_in_link')}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
